import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Form } from 'rsuite';
import { RsInput } from 'components/Rsuite/Input';
import { RsButton } from 'components/Rsuite/Button';
import { useCampaignStore } from 'store/campaignStore';
import { useProductStore } from 'store/productStore';
import { useFormik } from 'formik';
import WarningRoundIcon from '@rsuite/icons/WarningRound';
import IconWithTooltip from 'components/IconWithTooltip/IconWithTooltip';
import { faPencilAlt } from '@fortawesome/free-solid-svg-icons';
import i18n from 'i18n';
import { useOrderStore } from 'store/orderStore';
import {
  CreativeManager,
  DefaultCreativeManager,
} from 'core/creative/CreativeManager';
import { toast } from 'react-toastify';
import useCreativeStore from 'store/creativeStore';
import useCreativeForm from '../Hooks/useCreativeForm';
import useShopFormValidation from 'components/Rsuite/CreativeForm/Hooks/useShopFormValidation';
import { useCoreContext } from 'contexts/coreContext';
import _ from 'lodash';
import { RsSelect } from 'components/Rsuite/Select';
import { DefaultBrandManager } from 'core/brand/BrandManager';
import { userStore } from 'store/user';
import { ActionType } from 'core/rtbCampaign/singlePage/common';

const creativeManager: CreativeManager = new DefaultCreativeManager();
const defaultBrandManager = new DefaultBrandManager();

interface ShopFormProps {
  campaignId: string;
}

const ShopForm: FC<ShopFormProps> = ({ campaignId }) => {
  const core = useCoreContext();
  const rawAgencyId = _.get(core, 'accountManager.localeMeta.agencyId');
  const agencyId: string | undefined =
    rawAgencyId !== undefined ? String(rawAgencyId) : undefined;
  const { seller } = userStore();
  const { order } = useOrderStore();
  const {
    actionType: campaignActionType,
    brand,
    openProductManager,
    handleLoading,
  } = useCampaignStore();
  const { selectedProducts, changeSelectedProducts } = useProductStore();
  const { actionType, creative, openCreativeManager } = useCreativeStore();
  const { getCreativesByCampaignId } = useCreativeForm();

  const [cnCodeOptions, setCnCodeOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [hasFetchedCnCode, setHasFetchedCnCode] = useState<boolean>(false);

  const initialValues =
    actionType === 'edit'
      ? creative
      : {
          name: '',
          bannerUrl: '',
          bannerExtra: {
            brandName: '',
            categoryName: '',
            webBrandNo: undefined,
            categoryCode: '',
          },
          creativeType: 14,
          product: {},
        };
  const formik = useFormik({
    initialValues: initialValues,
    onSubmit: (values) => {
      switch (actionType) {
        case 'create':
          handleCreateCreative();
          break;
        case 'edit':
          if (campaignActionType === ActionType.EDIT) {
            handleUpdateCreative();
          } else {
            handleCreateCreative();
          }
          break;
        default:
          break;
      }
    },
  });

  const { errors, handleValidation } = useShopFormValidation({
    defaultForm: creative,
    values: formik.values,
  });

  const handleCreateCreative = async () => {
    useCreativeStore.setState({ creative: formik.values });
    openCreativeManager(false);
  };

  const handleUpdateCreative = async () => {
    handleLoading(true);
    try {
      const updateCrative = {
        ...formik.values,
        tenmaxCategory: 'e_commerce',
        webBrandNo: formik.values.webBrandNo,
        categoryCode: formik.values.categoryCode,
        typeProperties: {
          primaryProduct: formik.values.product,
        },
      };
      const formData = new FormData();
      if (agencyId) {
        formData.append('creative.agencyId', agencyId as string);
      } else {
        throw new Error('agencyId is undefined');
      }
      formData.append('creative.creativeType', updateCrative.creativeType);
      formData.append('creative.name', updateCrative.name);
      formData.append('creative.tenmaxCategory', 'e_commerce');
      formData.append('creative.bannerExtra', updateCrative.bannerExtra);
      formData.append('creative.bannerUrl', updateCrative.bannerUrl);
      formData.append('creative.landingPageUrl', updateCrative.landingPageUrl);
      formData.append('creative.retail', updateCrative.retail);
      formData.append('creative.id', updateCrative.creativeId);
      formData.append(
        'creative.webBrandNo',
        updateCrative.bannerExtra.webBrandNo,
      );
      formData.append(
        'creative.categoryCode',
        updateCrative.bannerExtra.categoryCode,
      );
      formData.append('creative.advertiserId', order.advertiserId.toString());
      Object.keys(updateCrative.typeProperties).forEach((key) => {
        if (key === 'primaryProduct') {
          formData.append(
            `creative.typeProperties[${key}]`,
            JSON.stringify(updateCrative.typeProperties[key]),
          );
        } else {
          formData.append(
            `creative.typeProperties[${key}]`,
            updateCrative.typeProperties[key],
          );
        }
      });
      const data = await creativeManager.updateCreative(formData);
      if (data) {
        toast.success('素材更新成功');
        getCreativesByCampaignId(campaignId);
        openCreativeManager(false);
      }
    } catch (error) {
      console.error(error);
      handleLoading(false);
    } finally {
      handleLoading(false);
    }
  };

  const fetchCnCodeOptions = useCallback(
    async (webBrandNo: string): Promise<void> => {
      if (hasFetchedCnCode || campaignActionType === ActionType.EDIT) return;
      handleLoading(true);
      try {
        const cnCodeOptions =
          await defaultBrandManager.getCnCodeOptions(webBrandNo);
        setCnCodeOptions(cnCodeOptions);

        if (
          cnCodeOptions.length > 0 &&
          !formik.values.bannerExtra.categoryCode
        ) {
          formik.setFieldValue('categoryCode', cnCodeOptions[0]);
        }
        setHasFetchedCnCode(true);
      } catch (error) {
        console.error('Failed to fetch data:', error);
      } finally {
        handleLoading(false);
      }
    },

    [hasFetchedCnCode, campaignActionType, formik],
  );

  const handleChangeBannerUrl = (value) => {
    const trimValue = value.replace(/\s/g, '');
    formik.setFieldValue('bannerUrl', trimValue);
    if (hideCnCode && trimValue.includes('cn=')) {
      const cnCodeMatch = trimValue.match(/cn=([0-9]{10,})(?=&|\b)/);
      if (cnCodeMatch) {
        formik.setFieldValue('bannerExtra.categoryCode', cnCodeMatch[1]);
      }
    }
  };

  const camPaignBrand = useMemo(() => {
    return brand;
  }, [brand]);

  const hideCnCode = useMemo(() => {
    if (seller !== '1P') return true;
    if (campaignActionType === ActionType.EDIT)
      return formik.values.bannerExtra.categoryName === null;

    return cnCodeOptions.length === 0;
  }, [
    seller,
    campaignActionType,
    formik.values.bannerExtra.categoryName,
    cnCodeOptions.length,
  ]);

  const hideUrl = useMemo(() => {
    if (seller === '3P') return true;
    if (seller === '1P') {
      if (campaignActionType === ActionType.EDIT)
        return formik.values.bannerExtra.categoryName !== null;
      return !formik.values.bannerExtra.webBrandNo || cnCodeOptions.length > 0;
    }
    return true;
  }, [
    seller,
    campaignActionType,
    formik.values.bannerExtra.categoryName,
    formik.values.bannerExtra.webBrandNo,
    cnCodeOptions.length,
  ]);

  const deleteProduct = () => {
    formik.setFieldValue('product', {});
    changeSelectedProducts(() => []);
  };

  useEffect(() => {
    if (
      brand.value &&
      seller === '1P' &&
      (campaignActionType === ActionType.CREATE ||
        campaignActionType === ActionType.COPY)
    ) {
      fetchCnCodeOptions(brand.value);
    }
  }, [seller, brand.value, campaignActionType]);

  useEffect(() => {
    return () => {
      formik.resetForm();
    };
  }, []);

  useEffect(() => {
    if (
      campaignActionType === ActionType.CREATE ||
      campaignActionType === ActionType.COPY
    ) {
      if (!_.isEmpty(selectedProducts)) {
        formik.setFieldValue('product', selectedProducts[0]);
      }
    }
  }, [selectedProducts]);

  useEffect(() => {
    handleValidation();
  }, [formik.values]);

  useEffect(() => {
    if (
      (campaignActionType === ActionType.CREATE ||
        campaignActionType === ActionType.COPY) &&
      camPaignBrand
    ) {
      formik.setFieldValue('bannerExtra.webBrandNo', camPaignBrand.value);
      formik.setFieldValue('bannerExtra.brandName', camPaignBrand.label);
    }
    if (actionType === 'create' && creative) {
      formik.setFieldValue('bannerExtra.categoryCode', '');
      formik.setFieldValue('bannerUrl', '');
    }
  }, [camPaignBrand, actionType, seller]);

  return (
    <>
      <Form.Group controlId='name'>
        <Form.ControlLabel>素材名稱</Form.ControlLabel>
        <RsInput
          value={formik.values.name}
          onChange={(value) => formik.setFieldValue('name', value)}
          className='!w-[420px]'
          errorMessage={errors?.name as string}
          disabled={formik.values.actionType === 'bind'}
        />
      </Form.Group>
      <Form.Group controlId='webBrandNo'>
        <Form.ControlLabel>品牌</Form.ControlLabel>
        <div className='flex'>
          {(campaignActionType === ActionType.CREATE ||
            campaignActionType === ActionType.COPY) && (
            <div className='w-full max-w-[420px] rounded border bg-[#f7f7fa] px-[12px] py-2'>
              <span className='inline-block text-[#999999]'>
                {formik.values.bannerExtra.brandName}
              </span>
            </div>
          )}
          {campaignActionType === ActionType.EDIT && (
            <div className='w-full max-w-[420px] rounded border bg-[#f7f7fa] px-[12px] py-2'>
              <span className='inline-block text-[#999999]'>
                {formik.values.bannerExtra.brandName}
              </span>
            </div>
          )}
        </div>
        <Form.HelpText className='text-[11px] text-[#98969d]'>
          此清單僅列出符合黃金置頂搜尋廣告資格的品牌
        </Form.HelpText>
      </Form.Group>
      {!hideCnCode && (
        <Form.Group controlId='name'>
          <Form.ControlLabel>旗艦館</Form.ControlLabel>
          <div className='flex'>
            {(campaignActionType === ActionType.CREATE ||
              campaignActionType === ActionType.COPY) && (
              <RsSelect
                data={cnCodeOptions}
                value={formik.values.bannerExtra.categoryCode}
                placeholder='請選擇'
                onChange={(value) =>
                  formik.setFieldValue('bannerExtra.categoryCode', value)
                }
                className='!w-[420px]'
                errorMessage={errors?.bannerExtra?.categoryCode as string}
                disabled={formik.values.actionType === 'bind'}
              />
            )}
            {campaignActionType === ActionType.EDIT && (
              <div className='w-full max-w-[420px] rounded border bg-[#f7f7fa] px-[12px] py-2'>
                <span className='inline-block text-[#999999]'>
                  {formik.values.bannerExtra.categoryName}
                </span>
              </div>
            )}
          </div>
        </Form.Group>
      )}
      {!hideUrl && (
        <Form.Group controlId='bannerUrl'>
          <Form.ControlLabel>連結網址</Form.ControlLabel>
          <div className='flex flex-col gap-2'>
            <RsInput
              value={formik.values.bannerUrl}
              onChange={handleChangeBannerUrl}
              errorMessage={errors?.bannerUrl as string}
              disabled={formik.values.actionType === 'bind'}
            />
            <p className='text-[11px] text-[#98969d]'>
              此欄位需輸入小網館架網址，您可將下方網址中
              <span className='text-red-500'>紅色數字置換成您的館架ID</span>
              後，貼上使用
              <br />
              https://m.momoshop.com.tw/category.momo?cn=
              <span className='text-red-500'>1234567890</span>
              &cid=dir&oid=product
              <br />
              請務必點選開啟連結按鈕確認網址是否正確
            </p>
          </div>
        </Form.Group>
      )}
      <Form.Group controlId='product'>
        <Form.ControlLabel>主打品</Form.ControlLabel>
        {!_.isEmpty(formik.values?.product) &&
          (campaignActionType === ActionType.CREATE ||
            campaignActionType === ActionType.COPY) &&
          formik.values.actionType !== 'bind' && (
            <div className='flex items-start gap-2'>
              <div className='float-left w-[420px] rounded border border-gray-300 px-2 py-3'>
                <span className='inline-block break-words border border-[#98969d] bg-[#f7f7f7] p-2 text-[#242328]'>
                  {formik.values?.product.name}
                  <WarningRoundIcon
                    className='ml-1 cursor-pointer text-[#413993]'
                    onClick={deleteProduct}
                  />
                </span>
              </div>
              <div>
                <IconWithTooltip
                  icon={faPencilAlt}
                  tooltipProps={{
                    id: `select-product-field-edit-products-hint`,
                    tooltip: i18n.t<string>(
                      'creativeSetupFlow.labels.editProductsHint',
                    ),
                  }}
                  onClick={() => openProductManager(true)}
                />
              </div>
            </div>
          )}
        {(campaignActionType === ActionType.EDIT ||
          formik.values.actionType === 'bind') && (
          <div className='flex items-start gap-2 py-2'>
            <span className='inline-block break-words text-left text-[#615f67]'>
              {formik.values?.product?.name}
            </span>
          </div>
        )}
        {(campaignActionType === ActionType.CREATE ||
          campaignActionType === ActionType.COPY) &&
          _.isEmpty(formik.values?.product) && (
            <div>
              <RsButton
                appearance='ghost'
                className='!border-[#615f67] !text-[#615f67]'
                onClick={() => openProductManager(true)}
                errorMessage={errors?.product as string}
              >
                選擇商品
              </RsButton>
              <Form.HelpText className='text-[11px] text-[#98969d]'>
                此主打品選擇與實際曝光商品無直接關聯
              </Form.HelpText>
            </div>
          )}
      </Form.Group>
      <div className='pb-4 pt-10'>
        <div className='flex items-center justify-start gap-4'>
          <RsButton
            appearance='primary'
            onClick={() => formik.handleSubmit()}
            disabled={!_.isEmpty(errors)}
          >
            <span className='text-[16px]'>完成</span>
          </RsButton>
        </div>
      </div>
    </>
  );
};

export default ShopForm;
